/**
 * 文字变形（Warp Text，对比 Photoshop 补全第一档缺口 阶段 5）。
 *
 * 真实 Photoshop 有十几种预设，背后是通用网格液化引擎（对整块文本框的
 * 网格施加变形）——这套代码库没有这个引擎（附录 A 没列），这里改用
 * "每个字形独立算一个垂直位移 + 旋转角"的闭式公式，代表性挑三种：
 * 弧形(arc)——单峰、两端夹角随曲率变化（抛物线近似圆弧）；
 * 凸起(bulge)——单峰但曲线族不同（正弦半周），跟弧形的差异在肩部曲率；
 * 波浪(wave)——一个完整 S 波（正弦全周），左右两端反向弯曲。
 * 不做 Photoshop 完整预设列表（旗形/鱼形/increase/膨胀…），已在完成
 * 报告披露这是简化版本。
 *
 * 每个字形的变形只看它在整行宽度上的归一化位置 `t`——不做真正的网格
 * 液化（没有局部拉伸/挤压字形本身的宽高），只挪位置+转角度，跟竖排
 * 文字里"整串旋转"是同一个数量级的简化（`engine/text/vertical.ts` 的
 * `VerticalGlyph.rotate` 就是这个思路的先例，这里把它从两个离散角度
 * 推广成连续角度，`rotationMatrix` 也跟着推广，见该文件）。
 */
export type WarpStyle = 'arc' | 'bulge' | 'wave';

export interface WarpParams {
  readonly style: WarpStyle;
  /** 弯曲度，−100–100（PS 语义：正值向上凸/上扬，负值反向）。 */
  readonly bend: number;
}

export interface WarpSample {
  /** 垂直位移，像素（正值向上，即 canvas y 减小方向）。 */
  readonly dy: number;
  /** 顺时针旋转角度，度——喂给 `engine/text/vertical.ts` 的 `rotationMatrix`。 */
  readonly angle: number;
}

/** 中心归一化到 [-0.5, 0.5] 的曲线族——三种预设的位移量（未乘振幅）与其对 t 的导数。 */
function curve(style: WarpStyle, t: number): { readonly y: number; readonly dydt: number } {
  const c = t - 0.5;
  if (style === 'arc') {
    // 抛物线近似圆弧：两端归零，中点最凸
    return { y: 1 - (2 * c) ** 2, dydt: -8 * c };
  }
  if (style === 'bulge') {
    // 正弦半周：跟弧形同为单峰，但肩部曲率不同（导数在两端不为零的方式不同）
    return { y: Math.sin(Math.PI * t), dydt: Math.PI * Math.cos(Math.PI * t) };
  }
  // wave：一个完整 S 波，左右端反向弯曲
  return { y: Math.sin(2 * Math.PI * t), dydt: 2 * Math.PI * Math.cos(2 * Math.PI * t) };
}

/**
 * 采样一个字形位置的变形。
 *
 * @param t 字形 baseline x 在整行宽度上的归一化位置，[0, 1]。
 * @param boxWidth 整行的像素宽度——把对 t 的导数换算成真实的 dy/dx 斜率，
 *   角度才跟实际字号/行宽相称（同样的 bend，窄行比宽行转得更陡）。
 * @param amplitude 弯曲振幅上限（bend=±100 时的最大垂直位移），像素。
 */
export function sampleWarp(
  params: WarpParams,
  t: number,
  boxWidth: number,
  amplitude: number,
): WarpSample {
  const k = Math.min(100, Math.max(-100, params.bend)) / 100;
  const { y, dydt } = curve(params.style, Math.min(1, Math.max(0, t)));
  const dy = -k * amplitude * y; // 负号：正 bend 是"向上凸"，canvas y 向下
  const slope = boxWidth > 0 ? (-k * amplitude * dydt) / boxWidth : 0;
  const angle = (Math.atan(slope) * 180) / Math.PI;
  return { dy, angle };
}
